// Default text for /marketing — mirrors backend MarketingIn defaults.
// The page uses `data.field || MK_DEFAULTS.field` so admin edits override.
// The admin editor uses these as placeholders.

export const MK_DEFAULTS = {
  // HERO
  hero_overline: "Our Approach",
  hero_title_lead: "How We Build ",
  hero_title_accent: "Marketing",
  hero_question: "What is marketing",
  hero_definition:
    "Marketing is shaping perception to influence decisions at scale using value and distribution.",

  // PILLARS
  pillars_overline: "3 Pillars",
  pillar_1_title: "Value",
  pillar_1_body: "What you offer",
  pillar_2_title: "Perception",
  pillar_2_body: "What people believe",
  pillar_3_title: "Distribution",
  pillar_3_body: "How people see you",

  // INTRO
  intro_title: "How Artistry Perspectives build marketing",
  intro_subtitle: "(in reality)",
  intro_line_1: "Let's say we are building a brand from scratch.",
  intro_line_2: "Example: Sim's Glow Cream (Skincare Brand)",
  bb_overline: "Brand Basics",
  bb_brand_name_label: "Brand Name",
  bb_brand_name_value: "Sim's Glow Cream",
  bb_logo_label: "Logo",
  bb_tagline_label: "Tagline",
  bb_tagline_value: '"Healthy skin, visible glow"',
  bb_mission_label: "Mission",
  bb_mission_value: "To help people achieve healthier, glowing skin",
  bb_vision_label: "Vision",
  bb_vision_value: "To become a trusted skincare brand",
  intro_question_lead: "Now the question is:",
  intro_question_quote: "How do we market this brand effectively?",
  intro_note:
    "Note: We'll use this example (Sim's Glow Cream) throughout to show how marketing works.",

  // 01 TARGET AUDIENCE
  s1_overline: "01",
  s1_title: "Target Audience",
  s1_emphasis: "Not everyone.",
  s1_body_1:
    "When it comes to targeting, the more specific we become, the better the marketing works.",
  s1_body_2:
    "These are the people most likely to buy — and bring the majority of your revenue.",
  s1_for_label: "For Sim's Glow Cream",
  s1_neg_1: "Not women",
  s1_neg_2: "Not women (18–25)",
  s1_right_label: "The right audience ✓",
  s1_right_headline: "Urban Women",
  s1_right_age: "Age 18–25",
  s1_right_trait_1: "Experiencing dull or uneven skin",
  s1_right_trait_2: "Who care about healthy, glowing skin",
  s1_specificity_note: "We have to be as specific as possible.",
  s1_final: "Now we know exactly who we are targeting.",

  // 02 INSIGHT
  s2_overline: "02",
  s2_title: "Insight",
  s2_body_1: "What your audience actually feels, struggles with, or desires.",
  s2_body_2: "Not assumptions. Real emotions.",
  s2_body_3:
    "This comes from understanding the audience deeply. (Should be backed by proper agency research, statistics and maintaining records along with recorded responses)",
  s2_quote: "People don't buy products.\nThey buy how they feel.",
  s2_intro_to_diagram: "We can communicate in two ways:",
  s2_pain_label: "Pain",
  s2_pain_q1: '"My skin looks dull and uneven"',
  s2_pain_q2: "\"I don't feel confident when my skin doesn't look good\"",
  s2_desire_label: "Desire",
  s2_desire_q1: '"I want clear, glowing skin"',
  s2_desire_q2: '"I want to feel confident about my appearance"',
  s2_final:
    "Now we have 2 ways to communicate to our target audience — through pain or desire (insight).",

  // 03 VALUE
  s3_overline: "03",
  s3_title: "Value",
  s3_body_1: "What your product offers.",
  s3_body_2: "Not just features — what it does for the audience.",
  s3_body_3: "Value should be built based on what people feel.",
  s3_feel_label: "What people feel (insight)",
  s3_feel_1: '"My skin looks dull" (pain)',
  s3_feel_2: '"I want glowing skin" (desire)',
  s3_offer_label: "What we offer",
  s3_offer_1: "Reduces dullness and uneven skin",
  s3_offer_2: "Improves skin hydration",
  s3_offer_3: "Enhances natural glow",
  s3_offer_4: "Improves skin texture",
  s3_connect_label: "How it connects",
  s3_connect_1: "Reduces dullness → speaks to pain",
  s3_connect_2: "Enhances glow → speaks to desire",
  s3_close_line: "We take our value and connect it to what people feel (insight).",
  s3_final:
    "Now we have a value that can be communicated through pain or desire (insight) to target audience.",

  // 04 POSITIONING
  s4_overline: "04",
  s4_title: "Positioning",
  s4_body_1: "Why someone should choose your brand over others.",
  s4_body_2: "Not what you say — what people remember, feel about you.",
  s4_body_3: "Positioning defines how your brand is perceived.",
  s4_examples_label: "Real brand examples",
  s4_for_label: "For Sim's Glow Cream",
  s4_for_intro: "Same product. Different positioning.",
  s4_opt_1: "Affordable skincare",
  s4_opt_2: "Dermatologist-backed skincare",
  s4_opt_3: "Natural, chemical-free skincare",
  s4_opt_4: "Premium skincare",
  s4_opt_or: "Or",
  s4_after_1: "Positioning one clear idea in their (audience) mind.",
  s4_after_2: "Each one feels different. Even if the product is similar.",
  s4_quote: "If your brand feels like everyone else, people won't remember you.",
  s4_close_1:
    "Your positioning should come from your audience and their needs (insight + value).",
  s4_close_2: "Premium is not just pricing — it's how your brand feels.",
  s4_final:
    "Now we know how the brand should feel (positioning) when communicating our value to the audience.",

  // 05 BRAND STRATEGY
  s5_overline: "05",
  s5_title: "Brand Strategy",
  s5_body_1: "Most people think brand strategy is about doing something very unique.",
  s5_quote_short: "It's not.",
  s5_body_2: "Brand strategy is how you consistently express your positioning.",
  s5_body_3: "Not one big idea — but repeating the right idea clearly, everywhere.",
  s5_body_4:
    "If positioning is what people think,\nbrand strategy is how you make them feel it again and again.",
  s5_card_for: "For Sim's Glow Cream",
  s5_card_positioning: "Positioning: Premium skincare for healthy glow",
  s5_card_label: "Brand Strategy",
  s5_tone_k: "Tone",
  s5_tone_v: "Clean, confident, informative",
  s5_personality_k: "Personality",
  s5_personality_v: "Trustworthy, modern",
  s5_messaging_k: "Messaging",
  s5_messaging_v: '"Healthy skin, visible glow"',
  s5_style_k: "Style",
  s5_style_v: "Minimal, premium visuals",
  s5_close_1:
    "This ensures your brand feels the same across everything, every marketing idea.",
  s5_close_2: "Without this, your content feels random.",
  s5_close_3: "With this, your brand becomes recognizable.",
  s5_arrow:
    "Strong brands are not built by doing different things every day. They are built by repeating the same idea consistently.",
  s5_final:
    "Now we take everything we've built — audience, insight, value, positioning — and make it consistent across every marketing touchpoint.",

  // 06 CONTENT
  s6_overline: "06",
  s6_title: "Content",
  s6_body_1: "This is what people actually see.",
  s6_body_2: "Content is how your brand communicates your value.",
  s6_body_3: "It is not random posts. It is not just trends.",
  s6_body_4:
    "It is how you express your value — based on your audience, their insight, your positioning, and your brand strategy.",
  s6_minibox_audience: "Urban women (18–25)",
  s6_minibox_insight: "Dull skin (pain), glowing skin (desire)",
  s6_minibox_value: "Improves hydration, enhances glow",
  s6_a_title: "A. Content connects to what people feel (insight)",
  s6_a_pain_q: '"My skin looks dull"',
  s6_a_pain_show: "Content → Show dull vs glowing skin",
  s6_a_pain_outcome: "→ Makes them feel understood (gives hope)",
  s6_a_desire_q: '"I want glowing skin"',
  s6_a_desire_show: "Content → Show transformation, routines, results",
  s6_a_desire_outcome: "→ Makes them want the outcome",
  s6_b_title: "B. To build trust",
  s6_b_line_1: "Content → Show real results, before/after",
  s6_b_line_2: "→ Makes them believe it works",
  s6_c_title: "C. To build understanding",
  s6_c_line_1: "Content → Explain skincare simply",
  s6_c_line_2: "→ Makes them feel confident to choose",
  s6_types_title: "Types of content",
  s6_purpose_title: "Each piece of content has a purpose",
  s6_purpose_1: "Make them feel understood",
  s6_purpose_2: "Make them want the outcome",
  s6_purpose_3: "Make them trust the product",
  s6_close_1: "Without this, content feels random.",
  s6_close_2: "With this, content drives decisions.",
  s6_arrow:
    "Good content doesn't just show your product. It makes people feel, trust, and buy.",
  s6_structure_title: "Content structure",
  s6_struct_hook_t: "Hook",
  s6_struct_hook_b: "Get attention",
  s6_struct_ret_t: "Retention",
  s6_struct_ret_b: "Keep attention",
  s6_struct_value_t: "Value",
  s6_struct_value_b: "Give value",
  s6_final:
    "Now we are not just posting content — we are influencing how people feel, think, and decide.",

  // 07 DISTRIBUTION
  s7_overline: "07",
  s7_title: "Distribution",
  s7_subtitle:
    "(limited to social media for easy understanding, rest not explained)",
  s7_body_1: "Not logistics. Not delivery.",
  s7_body_2: "Distribution is about attention.",
  s7_body_3: "Content is not enough. People need to see it.",
  s7_body_4: "Distribution decides how many people see your content.",
  s7_body_5: "Distribution is how your content reaches the right audience.",
  s7_where_label: "Where are they?",
  s7_where_1: "Instagram",
  s7_where_2: "YouTube",
  s7_where_3: "Influencers",
  s7_where_4: "Skincare creators",
  s7_we_label: "So we distribute here",
  s7_we_1: "Instagram reels",
  s7_we_2: "YouTube videos",
  s7_we_3: "Influencer collaborations",
  s7_we_4: "Targeted ads",
  s7_platforms_title: "How platforms work (simple)",
  s7_ig_title: "Instagram",
  s7_ig_lead: "Instagram wants people to stay on the app. So it pushes content that:",
  s7_ig_b1: "people watch fully",
  s7_ig_b2: "people engage with",
  s7_ig_b3: "people share",
  s7_ig_close:
    "If your content is interesting, Instagram will push it to more people. If your content is boring, it stops there.",
  s7_ig_arrow: "The platform rewards content that keeps attention.",
  s7_yt_title: "YouTube",
  s7_yt_lead: "YouTube works the same way — but goes deeper. It pushes content based on:",
  s7_yt_b1: "watch time",
  s7_yt_b2: "retention",
  s7_yt_close: "If people keep watching, YouTube keeps pushing.",
  s7_summary_1:
    "Different platforms. Different behaviour. With same goal — keep people engaged.",
  s7_summary_2:
    "Platforms push content that keeps attention (retention), not just views.",
  s7_quote:
    "Good content in the wrong place will fail.\nAverage content in the right place can win.",
  s7_arrow: "Visibility drives growth.",
  s7_final:
    "Now we are reaching the right audience with the right content — at the right place (distribution).",

  // 08 FUNNEL
  s8_overline: "08",
  s8_title: "Funnel & Conversion",
  s8_body_1: "Getting attention is not the end.",
  s8_quote_1: "It's the beginning.",
  s8_body_2: "A funnel is how people move from seeing your content to becoming a customer.",
  s8_body_3: "People don't buy instantly. They go through a journey:",
  s8_for_label: "For Sim's Glow Cream",
  s8_step1_t: "See",
  s8_step1_b: "They discover your content (Instagram / YouTube)",
  s8_step2_t: "Understand",
  s8_step2_b:
    "They relate to the problem (dull skin)\nThey understand your value (enhances natural glow)",
  s8_step3_t: "Trust",
  s8_step3_b: "They see proof, results, testimonials\nThey feel confident",
  s8_step4_t: "Decide",
  s8_step4_b: "They compare, think, evaluate (positioning + strategy helps here)",
  s8_step5_t: "Buy",
  s8_step5_b: "They take action",
  s8_journey_eq: "This entire journey = Funnel",
  s8_conv_1: "Conversion is the final step.",
  s8_conv_quote: "It's when attention becomes revenue.",
  s8_without_t: "Without a funnel",
  s8_without_b: "People see → forget",
  s8_with_t: "With a funnel",
  s8_with_b: "People see → trust → buy",
  s8_arrow_1: "Content brings attention",
  s8_arrow_2: "Funnel builds trust",
  s8_arrow_3: "Conversion brings revenue",
  s8_final:
    "Now we are not just reaching people — we are turning attention into customers.",

  // CUSTOMER PSYCHOLOGY
  psy_overline: "—",
  psy_title: "Customer Psychology (basics)",
  psy_section_h: "How people decide",
  psy_quote: "People buy emotionally — and justify rationally later.",
  psy_drives_label: "What drives decisions",
  psy_drive_1_k: "Pain",
  psy_drive_1_v: '→ "I hate this problem"',
  psy_drive_2_k: "Desire",
  psy_drive_2_v: '→ "I want this outcome"',
  psy_drive_3_k: "Fear",
  psy_drive_3_v: "→ missing out on offers, trends",
  psy_drive_4_k: "Identity",
  psy_drive_4_v: '→ "I am this kind of person"',
  psy_trust_label: "What builds trust",
  psy_trust_1_k: "Social proof",
  psy_trust_1_v: "→ Others are using it",
  psy_trust_2_k: "Scarcity",
  psy_trust_2_v: "→ Limited = valuable",
  psy_trust_3_k: "Authority",
  psy_trust_3_v: "→ Experts recommend it",
  psy_arrow: "Good marketing connects to how people feel.",

  // OFFER
  off_overline: "—",
  off_title: "Offer",
  off_body_1: "Why someone says yes.",
  off_body_2: "A strong offer is not just price. It is:",
  off_value_t: "Value",
  off_value_b: "What they get",
  off_clarity_t: "Clarity",
  off_clarity_b: "Easy to understand",
  off_risk_t: "Risk reduction",
  off_risk_b: "Removes fear",
  off_urg_t: "Urgency",
  off_urg_b: "Reason to act now",
  off_arrow_1: "A weak offer kills good marketing.",
  off_arrow_2: "A strong offer improves conversion.",

  // FULL SYSTEM CLOSE
  sys_title: "The full system",
  sys_body_1: "Marketing is not a task.",
  sys_quote: "It's a system — and every part matters.",
  sys_equation_1: "Brand",
  sys_equation_2: "Perception",
  sys_equation_3: "Consistency",
  sys_equation_4: "Distribution",
  sys_close_1:
    "Brand is built when perception, consistency, and distribution work together.",
  sys_arrow: "When all parts work together, marketing starts working.",

  // CTA
  cta_overline: "Built this way for every brand we work with",
  cta_headline: "Have a brand worth building?",
  cta_button: "Start a conversation →",

  // DISCLAIMER
  disclaimer:
    "© Artistry Perspectives 2026. Property of Artistry Perspectives Studio. Shared for the perspective of brand owners and as an educational reference — open viewing access for students.",
};

/**
 * Group fields by section so the admin editor can render collapsible groups.
 * The key is the group's display name; value is an array of field IDs.
 */
export const MK_SECTIONS = [
  {
    id: "hero",
    label: "Hero",
    fields: ["hero_overline", "hero_title_lead", "hero_title_accent", "hero_question", "hero_definition"],
  },
  {
    id: "pillars",
    label: "3 Pillars strip",
    fields: ["pillars_overline", "pillar_1_title", "pillar_1_body", "pillar_2_title", "pillar_2_body", "pillar_3_title", "pillar_3_body"],
  },
  {
    id: "intro",
    label: "Intro · Brand basics",
    fields: ["intro_title", "intro_subtitle", "intro_line_1", "intro_line_2", "bb_overline", "bb_brand_name_label", "bb_brand_name_value", "bb_logo_label", "bb_tagline_label", "bb_tagline_value", "bb_mission_label", "bb_mission_value", "bb_vision_label", "bb_vision_value", "intro_question_lead", "intro_question_quote", "intro_note"],
  },
  {
    id: "s1",
    label: "01 · Target Audience",
    fields: ["s1_overline", "s1_title", "s1_emphasis", "s1_body_1", "s1_body_2", "s1_for_label", "s1_neg_1", "s1_neg_2", "s1_right_label", "s1_right_headline", "s1_right_age", "s1_right_trait_1", "s1_right_trait_2", "s1_specificity_note", "s1_final"],
  },
  {
    id: "s2",
    label: "02 · Insight",
    fields: ["s2_overline", "s2_title", "s2_body_1", "s2_body_2", "s2_body_3", "s2_quote", "s2_intro_to_diagram", "s2_pain_label", "s2_pain_q1", "s2_pain_q2", "s2_desire_label", "s2_desire_q1", "s2_desire_q2", "s2_final"],
  },
  {
    id: "s3",
    label: "03 · Value",
    fields: ["s3_overline", "s3_title", "s3_body_1", "s3_body_2", "s3_body_3", "s3_feel_label", "s3_feel_1", "s3_feel_2", "s3_offer_label", "s3_offer_1", "s3_offer_2", "s3_offer_3", "s3_offer_4", "s3_connect_label", "s3_connect_1", "s3_connect_2", "s3_close_line", "s3_final"],
  },
  {
    id: "s4",
    label: "04 · Positioning",
    fields: ["s4_overline", "s4_title", "s4_body_1", "s4_body_2", "s4_body_3", "s4_examples_label", "s4_for_label", "s4_for_intro", "s4_opt_1", "s4_opt_2", "s4_opt_3", "s4_opt_4", "s4_opt_or", "s4_after_1", "s4_after_2", "s4_quote", "s4_close_1", "s4_close_2", "s4_final"],
  },
  {
    id: "s5",
    label: "05 · Brand Strategy",
    fields: ["s5_overline", "s5_title", "s5_body_1", "s5_quote_short", "s5_body_2", "s5_body_3", "s5_body_4", "s5_card_for", "s5_card_positioning", "s5_card_label", "s5_tone_k", "s5_tone_v", "s5_personality_k", "s5_personality_v", "s5_messaging_k", "s5_messaging_v", "s5_style_k", "s5_style_v", "s5_close_1", "s5_close_2", "s5_close_3", "s5_arrow", "s5_final"],
  },
  {
    id: "s6",
    label: "06 · Content",
    fields: ["s6_overline", "s6_title", "s6_body_1", "s6_body_2", "s6_body_3", "s6_body_4", "s6_minibox_audience", "s6_minibox_insight", "s6_minibox_value", "s6_a_title", "s6_a_pain_q", "s6_a_pain_show", "s6_a_pain_outcome", "s6_a_desire_q", "s6_a_desire_show", "s6_a_desire_outcome", "s6_b_title", "s6_b_line_1", "s6_b_line_2", "s6_c_title", "s6_c_line_1", "s6_c_line_2", "s6_types_title", "s6_purpose_title", "s6_purpose_1", "s6_purpose_2", "s6_purpose_3", "s6_close_1", "s6_close_2", "s6_arrow", "s6_structure_title", "s6_struct_hook_t", "s6_struct_hook_b", "s6_struct_ret_t", "s6_struct_ret_b", "s6_struct_value_t", "s6_struct_value_b", "s6_final"],
  },
  {
    id: "s7",
    label: "07 · Distribution",
    fields: ["s7_overline", "s7_title", "s7_subtitle", "s7_body_1", "s7_body_2", "s7_body_3", "s7_body_4", "s7_body_5", "s7_where_label", "s7_where_1", "s7_where_2", "s7_where_3", "s7_where_4", "s7_we_label", "s7_we_1", "s7_we_2", "s7_we_3", "s7_we_4", "s7_platforms_title", "s7_ig_title", "s7_ig_lead", "s7_ig_b1", "s7_ig_b2", "s7_ig_b3", "s7_ig_close", "s7_ig_arrow", "s7_yt_title", "s7_yt_lead", "s7_yt_b1", "s7_yt_b2", "s7_yt_close", "s7_summary_1", "s7_summary_2", "s7_quote", "s7_arrow", "s7_final"],
  },
  {
    id: "s8",
    label: "08 · Funnel & Conversion",
    fields: ["s8_overline", "s8_title", "s8_body_1", "s8_quote_1", "s8_body_2", "s8_body_3", "s8_for_label", "s8_step1_t", "s8_step1_b", "s8_step2_t", "s8_step2_b", "s8_step3_t", "s8_step3_b", "s8_step4_t", "s8_step4_b", "s8_step5_t", "s8_step5_b", "s8_journey_eq", "s8_conv_1", "s8_conv_quote", "s8_without_t", "s8_without_b", "s8_with_t", "s8_with_b", "s8_arrow_1", "s8_arrow_2", "s8_arrow_3", "s8_final"],
  },
  {
    id: "psy",
    label: "Customer Psychology",
    fields: ["psy_overline", "psy_title", "psy_section_h", "psy_quote", "psy_drives_label", "psy_drive_1_k", "psy_drive_1_v", "psy_drive_2_k", "psy_drive_2_v", "psy_drive_3_k", "psy_drive_3_v", "psy_drive_4_k", "psy_drive_4_v", "psy_trust_label", "psy_trust_1_k", "psy_trust_1_v", "psy_trust_2_k", "psy_trust_2_v", "psy_trust_3_k", "psy_trust_3_v", "psy_arrow"],
  },
  {
    id: "off",
    label: "Offer",
    fields: ["off_overline", "off_title", "off_body_1", "off_body_2", "off_value_t", "off_value_b", "off_clarity_t", "off_clarity_b", "off_risk_t", "off_risk_b", "off_urg_t", "off_urg_b", "off_arrow_1", "off_arrow_2"],
  },
  {
    id: "sys",
    label: "Full system / closing",
    fields: ["sys_title", "sys_body_1", "sys_quote", "sys_equation_1", "sys_equation_2", "sys_equation_3", "sys_equation_4", "sys_close_1", "sys_arrow"],
  },
  {
    id: "cta",
    label: "CTA & Disclaimer",
    fields: ["cta_overline", "cta_headline", "cta_button", "disclaimer"],
  },
  {
    id: "highlights",
    label: "Highlight rules (gold + underline words)",
    fields: ["gold_words", "underline_words"],
  },
];

/** Pretty label for a field id (used by admin editor as the input label). */
export function fieldLabel(id) {
  return id
    .replace(/^s\d+_/, "")
    .replace(/^bb_/, "")
    .replace(/^psy_/, "")
    .replace(/^off_/, "")
    .replace(/^sys_/, "")
    .replace(/^cta_/, "")
    .replace(/^intro_/, "")
    .replace(/^pillars?_/, "")
    .replace(/^hero_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
